import { useEffect, useRef, useState } from 'react'
import { useNavigation } from 'react-router'
import { useSpinDelay } from 'spin-delay'
import { Icon } from '#app/components/ui/icon'
import { cn } from '#app/utils/misc'

export function ProgressBar() {
	const transition = useNavigation()
	const busy = transition.state !== 'idle'
	const delayedPending = useSpinDelay(busy, {
		delay: 400,
		minDuration: 300,
	})

	const [animationComplete, setAnimationComplete] = useState(true)
	const prevDelayedPending = usePrevious(delayedPending)

	const progressBarRef = useRef<HTMLDivElement>(null)
	const progressBarTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		if (!delayedPending && prevDelayedPending) {
			// Delay setting animationComplete to true to allow the progress bar to finish animating
			progressBarTimeoutRef.current = setTimeout(() => {
				setAnimationComplete(true)
			}, 200)
		} else if (delayedPending) {
			setAnimationComplete(false)
			if (progressBarTimeoutRef.current) {
				clearTimeout(progressBarTimeoutRef.current)
				progressBarTimeoutRef.current = null
			}
		}
	}, [delayedPending, prevDelayedPending])

	return (
		<>
			<div
				role="progressbar"
				aria-hidden={!delayedPending}
				aria-valuetext={delayedPending ? 'Loading' : undefined}
				className="fixed inset-x-0 left-0 top-0 z-50 h-1 animate-pulse"
			>
				<div
					ref={progressBarRef}
					className={cn(
						'h-full bg-primary transition-all duration-500 ease-in-out',
						delayedPending ? 'w-4/12' : 'w-full',
						animationComplete && !delayedPending ? 'opacity-0' : 'opacity-100',
					)}
				/>
			</div>
			{delayedPending ? (
				<div className="fixed bottom-4 right-4 z-50 flex items-center justify-center rounded-full bg-primary p-2 text-primary-foreground shadow-md">
					<Icon name="update" className="h-5 w-5 animate-spin" />
				</div>
			) : null}
		</>
	)
}

// Helper hook to get the previous value of a variable
function usePrevious<T>(value: T): T | undefined {
	const ref = useRef<T>(value)
	useEffect(() => {
		ref.current = value
	}, [value])
	return ref.current
}
